import React, { useState, useEffect, useRef, useCallback } from "react";
import { Sparkles, Send, Database, ArrowUp, Loader2, BarChart3, Lightbulb, Target } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { creditErrorMessage } from "@/lib/creditErrors";
import { OrgCreditBalance, ActionCreditPrice } from "@/components/LodgeCredits";
import DemoAskLodge from "@/components/demo/DemoAskLodge";
import { DEMO_MAURITIUS_ASK_QUESTIONS, isMauritiusDemoOrg } from "@/lib/demoMauritius";

const SECTION_META = {
  DATA: { label: "DATA", icon: BarChart3, cls: "border-[#A7F3D0] bg-[#F0FDF4]", tone: "text-[#047857]" },
  ANALYSIS: { label: "ANALYSIS", icon: Lightbulb, cls: "border-[#FDE68A] bg-[#FFFBEB]", tone: "text-[#B45309]" },
  RECOMMENDATION: { label: "RECOMMENDATION", icon: Target, cls: "border-[#BBD8C5] bg-[#F1F7F3]", tone: "text-[#2C5540]" },
};

function parseSections(text) {
  const sections = [];
  const re = /\*\*(DATA|ANALYSIS|RECOMMENDATION)\*\*\s*:?\s*/gi;
  const matches = [...text.matchAll(re)];
  if (matches.length === 0) return null;
  for (let i = 0; i < matches.length; i++) {
    const key = matches[i][1].toUpperCase();
    const start = matches[i].index + matches[i][0].length;
    const end = i + 1 < matches.length ? matches[i + 1].index : text.length;
    sections.push({ key, body: text.slice(start, end).trim() });
  }
  return sections;
}

function renderInline(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((chunk, i) => {
    if (chunk.startsWith("**") && chunk.endsWith("**")) {
      return <strong key={i} className="font-semibold text-[#17201A]">{chunk.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{chunk}</React.Fragment>;
  });
}

const AnswerBlock = ({ text }) => {
  const sections = parseSections(text);
  if (!sections) {
    return <p className="whitespace-pre-wrap text-sm leading-relaxed text-[#475569]">{renderInline(text)}</p>;
  }
  return (
    <div className="space-y-3">
      {sections.map((s, i) => {
        const meta = SECTION_META[s.key] || SECTION_META.DATA;
        const Icon = meta.icon;
        return (
          <div key={i} data-testid={`answer-section-${s.key}`} className={`rounded-xl border p-4 ${meta.cls}`}>
            <div className={`mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest ${meta.tone}`}>
              <Icon size={13} /> {meta.label}
            </div>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-[#374151]">{renderInline(s.body)}</p>
          </div>
        );
      })}
    </div>
  );
};

export default function AskLodge() {
  const { activeMembership } = useAuth();
  const org = activeMembership?.org;
  if (isMauritiusDemoOrg(org)) return <DemoAskLodge questions={DEMO_MAURITIUS_ASK_QUESTIONS} />;
  if (org?.is_demo && org?.demo_mode === "immutable") return <DemoAskLodge />;
  return <AskLodgeLive />;
}

function AskLodgeLive() {
  const { activeOrgId } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aiAvailable, setAiAvailable] = useState(false); // fail-closed until backend confirms
  const endRef = useRef(null);

  useEffect(() => {
    if (!activeOrgId) return;
    api.get(`/organizations/${activeOrgId}/ask-lodge/suggestions`)
      .then(({ data }) => setSuggestions(data.suggestions || []))
      .catch(() => setSuggestions([]));
  }, [activeOrgId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const ask = useCallback(async (question) => {
    const q = (question ?? input).trim();
    // Hard-gate: no request is ever emitted while loading (anti double-click),
    // without an org, or when the paid AI action is unavailable.
    if (!q || loading || !activeOrgId || !aiAvailable) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    // ONE stable idempotency key for this deliberate question. It is reused for a
    // transient network retry of the SAME question; a genuinely new question gets
    // a fresh key. The key is a header only — never in the URL, UI or console.
    const idemKey = crypto.randomUUID();
    const attempt = () => api.post(
      `/organizations/${activeOrgId}/ask-lodge`,
      { question: q },
      { headers: { "X-Idempotency-Key": idemKey } },
    );
    try {
      let data;
      try {
        ({ data } = await attempt());
      } catch (err) {
        // Only a genuine network/timeout error (no HTTP response) is retried, once,
        // reusing the SAME key. Controlled errors (with a response) are never retried.
        if (!err?.response) {
          ({ data } = await attempt());
        } else {
          throw err;
        }
      }
      if (data?.status === "already_completed") {
        setMessages((m) => [...m, { role: "assistant", text: "Cette action a déjà été traitée." }]);
      } else if (data?.status === "already_failed") {
        setMessages((m) => [...m, { role: "assistant", text: "La tentative précédente a échoué et a été remboursée. Relancez une nouvelle demande." }]);
      } else {
        setMessages((m) => [...m, { role: "assistant", text: data.answer }]);
      }
    } catch (err) {
      const text = creditErrorMessage(err, "Une erreur est survenue lors de l'analyse. Réessayez dans un instant.");
      setMessages((m) => [...m, { role: "assistant", text }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, activeOrgId, aiAvailable]);

  const empty = messages.length === 0;

  return (
    <div className="flex h-full flex-col" data-testid="ask-lodge-page">
      {/* Header */}
      <div className="mb-6">
        {activeOrgId && (
          <div className="mb-4 space-y-2">
            <OrgCreditBalance orgId={activeOrgId} />
            <ActionCreditPrice orgId={activeOrgId} actionType="ask_lodge" label="Ask Lodge" onAvailability={setAiAvailable} />
          </div>
        )}
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#1E3D2B] to-[#2C5540]">
            <Sparkles size={18} className="text-[#5C9873]" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-[#17201A]">Ask Lodge</h1>
            <p className="text-sm text-[#64748B]">Votre assistant IA — répond à partir des données de votre organisation.</p>
          </div>
        </div>
      </div>

      {/* Conversation */}
      <div className="flex-1 space-y-6 overflow-y-auto pb-4" data-testid="ask-lodge-thread">
        {empty ? (
          <div className="mx-auto max-w-xl py-10 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F1F7F3]">
              <Sparkles size={26} className="text-[#3E7256]" />
            </div>
            <h2 className="font-heading text-lg font-semibold text-[#17201A]">Posez une question sur vos données</h2>
            <p className="mx-auto mt-1 max-w-md text-sm text-[#64748B]">
              Chaque réponse distingue clairement <strong>DATA</strong> (issu des données), <strong>ANALYSIS</strong> (interprétation) et <strong>RECOMMENDATION</strong> (action). Ask Lodge n’invente jamais une donnée absente.
            </p>
            <div className="mt-6 flex flex-col gap-2">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  data-testid={`suggestion-${i}`}
                  onClick={() => ask(s)}
                  className="group flex items-center justify-between gap-3 rounded-xl border border-[#E8E5DD] bg-white px-4 py-3 text-left text-sm font-medium text-[#374151] transition-colors hover:border-[#5C9873] hover:bg-[#FAFAF6]"
                >
                  {s}
                  <ArrowUp size={15} className="rotate-45 text-slate-300 transition-colors group-hover:text-[#3E7256]" />
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="mx-auto max-w-3xl space-y-6">
            {messages.map((m, i) => (
              m.role === "user" ? (
                <div key={i} className="flex justify-end" data-testid={`msg-user-${i}`}>
                  <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-[#1E3D2B] px-4 py-2.5 text-sm font-medium text-white">
                    {m.text}
                  </div>
                </div>
              ) : (
                <div key={i} className="flex items-start gap-3" data-testid={`msg-assistant-${i}`}>
                  <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#1E3D2B] to-[#2C5540]">
                    <Sparkles size={15} className="text-[#5C9873]" />
                  </div>
                  <div className="min-w-0 flex-1"><AnswerBlock text={m.text} /></div>
                </div>
              )
            ))}
            {loading && (
              <div className="flex items-center gap-3 text-sm text-slate-400" data-testid="ask-lodge-loading">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#F1F7F3]">
                  <Loader2 size={15} className="animate-spin text-[#3E7256]" />
                </div>
                Analyse de vos données…
              </div>
            )}
            <div ref={endRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <div className="mx-auto w-full max-w-3xl pt-2">
        <form
          onSubmit={(e) => { e.preventDefault(); ask(); }}
          className="flex items-center gap-2 rounded-2xl border border-[#E8E5DD] bg-white p-2 shadow-sm focus-within:border-[#5C9873]"
        >
          <input
            data-testid="ask-lodge-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Posez votre question à Ask Lodge…"
            className="flex-1 bg-transparent px-3 py-2 text-sm text-[#17201A] outline-none placeholder:text-slate-400"
          />
          <button
            data-testid="ask-lodge-send"
            type="submit"
            disabled={loading || !input.trim() || !aiAvailable}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#3E7256] text-white transition-colors hover:bg-[#2F5A43] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </form>
        <p className="mt-2 flex items-center justify-center gap-1.5 text-center text-[11px] text-slate-400">
          <Database size={11} /> Réponses fondées uniquement sur les données de votre organisation.
        </p>
      </div>
    </div>
  );
}
